import React, { useState, useCallback, useEffect, useMemo } from 'react';

function isObject(val) {
  return val && typeof val === 'object' && !Array.isArray(val);
}

function isArray(val) {
  return Array.isArray(val);
}

function getAllDescendantPaths(value, path = []) {
  let paths = [];
  if (isObject(value)) {
    Object.keys(value).forEach((k) => {
      const childPath = [...path, k];
      paths.push(childPath.join('.'));
      paths = paths.concat(getAllDescendantPaths(value[k], childPath));
    });
  } else if (isArray(value)) {
    value.forEach((item, idx) => {
      const childPath = [...path, idx];
      paths.push(childPath.join('.'));
      paths = paths.concat(getAllDescendantPaths(item, childPath));
    });
  }
  return paths;
}

function ExpandableNode({ label, value, path, expandedPaths, setExpandedPaths }) {
  const isExpandable = isObject(value) || isArray(value);
  const nodePath = path.join('.');
  const open = expandedPaths.has(nodePath);

  const handleToggle = useCallback(() => {
    if (open) {
      // Collapse this node and all descendants
      const allDescendants = getAllDescendantPaths(value, path);
      setExpandedPaths((prev) => {
        const next = new Set(prev);
        next.delete(nodePath);
        allDescendants.forEach((desc) => next.delete(desc));
        return next;
      });
    } else {
      // Expand this node and all descendants
      const allDescendants = getAllDescendantPaths(value, path);
      setExpandedPaths((prev) => {
        const next = new Set(prev);
        next.add(nodePath);
        allDescendants.forEach((desc) => next.add(desc));
        return next;
      });
    }
  }, [open, nodePath, setExpandedPaths, value, path]);

  return (
    <div style={{ marginLeft: 16, fontFamily: 'monospace', fontSize: 14 }}>
      {isExpandable ? (
        <div>
          <span style={{ cursor: 'pointer', fontWeight: 'bold', color: '#0074D9' }} onClick={handleToggle}>
            {open ? '▼' : '▶'} {label}
            {isArray(value) ? ' [ ]' : ' { }'}
          </span>
          {open && (
            <div style={{ marginLeft: 16 }}>
              {isArray(value)
                ? value.map((item, idx) => (
                    <ExpandableNode
                      key={idx}
                      label={idx}
                      value={item}
                      path={[...path, idx]}
                      expandedPaths={expandedPaths}
                      setExpandedPaths={setExpandedPaths}
                    />
                  ))
                : Object.keys(value).map((k) => (
                    <ExpandableNode
                      key={k}
                      label={k}
                      value={value[k]}
                      path={[...path, k]}
                      expandedPaths={expandedPaths}
                      setExpandedPaths={setExpandedPaths}
                    />
                  ))}
            </div>
          )}
        </div>
      ) : (
        <span>
          <span style={{ color: '#555' }}>{label}:</span> <span style={{ color: '#222' }}>{JSON.stringify(value)}</span>
        </span>
      )}
    </div>
  );
}

export default function JSONViewer({ json }) {
  // Memoize parsed data to avoid unnecessary re-parsing and to use as a dependency
  const data = useMemo(() => {
    try {
      return typeof json === 'string' ? JSON.parse(json) : json;
    } catch (e) {
      return { __error: e };
    }
  }, [json]);

  // Compute all paths for full expansion
  function getAllPaths(data) {
    return ['root', ...getAllDescendantPaths(data, ['root'])];
  }

  const [expandedPaths, setExpandedPaths] = useState(() => new Set(getAllPaths(data)));

  useEffect(() => {
    setExpandedPaths(new Set(getAllPaths(data)));
  }, [data]);

  // If the JSON is invalid, show error
  if (data && data.__error) {
    return (
      <div style={{ color: 'red', fontFamily: 'monospace', padding: 16 }}>Invalid JSON: {data.__error.message}</div>
    );
  }

  return (
    <div style={{ padding: 16, fontFamily: 'monospace', fontSize: 14 }}>
      <ExpandableNode
        label="root"
        value={data}
        path={['root']}
        expandedPaths={expandedPaths}
        setExpandedPaths={setExpandedPaths}
      />
    </div>
  );
}
